import type { SonataDatabase } from './db.js';
import type { MutationOperation } from '../types.js';
import {routePayload} from './routeRecorder.js';

const workspaceRecord = (table: string, row: any) => {
  if (table !== 'gpsTrackPoints' || !row) return row;
  const number = (value: any) => value == null ? undefined : Number(value);
  return { ...row, routeId:row.routeId||row.route_id,ownerEmail:row.ownerEmail||row.owner_email,farmId:row.farmId||row.farm_id,sessionId: row.sessionId || row.session_id, ts: Number(row.ts),
    accuracyM: number(row.accuracyM ?? row.accuracy_m), headingDeg: number(row.headingDeg ?? row.heading_deg),
    speedMps: number(row.speedMps ?? row.speed_mps), createdAt: row.createdAt || row.created_at };
};
export class WorkspaceSyncEngine {
  constructor(private database: SonataDatabase, private account: string,
    private request: (path: string, body?: any) => Promise<Response>, private current: () => boolean) {}
  private check() { if (!this.current()) throw new Error('Workspace changed; sync stopped.'); }
  async recoverInterrupted() {
    this.check();
    await this.database.outbox.where('status').equals('processing').modify({ status: 'pending' });
  }
  async push() {
    const database = this.database;
    const operations = await database.outbox.orderBy('created_at_local').toArray();
    const blocked = new Set<string>();
    for (const original of operations) {
      this.check();
      const op = await database.outbox.get(original.op_id);
      if (!op) continue;
      const key = `${op.table_name}:${op.entity_id}`;
      if (blocked.has(key) || op.status === 'conflict') { blocked.add(key); continue; }
      if (op.owner_account !== this.account) {
        await database.outbox.update(op.op_id, { status: 'failed', error: 'This edit belongs to another account.' });
        blocked.add(key); continue;
      }
      try {
        await database.outbox.update(op.op_id, { status: 'processing', error: undefined });
        const response = await this.request('/sync/mutate', op);
        this.check();
        if (response.status === 409) {
          const data = await response.json(); this.check();
          await database.outbox.update(op.op_id, { status: 'conflict', server_entity: workspaceRecord(op.table_name, data.serverEntity || data.results?.[0]?.entity),
            error: 'Another device changed this record. Your edit is retained; choose which version to keep.' });
          blocked.add(key); continue;
        }
        if (!response.ok) {
          const rejection = await response.json().catch(() => null);
          throw new Error(response.status === 401 ? 'Sign in to sync your saved edits.' : 'Sync request failed (' + response.status + '): ' + (rejection?.message || rejection?.error || 'Server rejected this edit.'));
        }
        const data = await response.json(); this.check();
        const entity = workspaceRecord(op.table_name, data.results?.[0]?.entity || data.entity);
        if (!entity) throw new Error('Server did not confirm the saved record.');
        await database.transaction('rw', [database.table(op.table_name), database.outbox], async () => {
          const successors = (await database.outbox.toArray()).filter(next => next.op_id !== op.op_id && next.table_name === op.table_name && next.entity_id === op.entity_id);
          // Acknowledging an older edit must never overwrite a newer local edit.
          if (!successors.length) {
            if(op.table_name==='recordedRoutes'){
              const local=await database.recordedRoutes.get(op.entity_id);
              await database.recordedRoutes.put({...entity,needsSegment:local?.needsSegment});
            }else await database.table(op.table_name).put(entity);
          }
          else {
            for (const next of successors) await database.outbox.update(next.op_id, { base_version: entity.server_version });
            await database.table(op.table_name).update(op.entity_id, { server_version: entity.server_version });
          }
          await database.outbox.delete(op.op_id);
        });
      } catch (error) {
        await database.outbox.update(op.op_id, { status: 'failed', retry_count: (op.retry_count || 0) + 1, error: (error as Error).message });
        blocked.add(key);
        if (!this.current()) throw error;
        // Stop on connection/auth failure; retry safely with the same mutation id.
        if (!(error instanceof Error) || !error.message.includes('Sync request failed')) break;
      }
    }
  }
  async pull() {
    this.check();
    const database = this.database;
    const state = await database.syncState.get('global_sync');
    const query = state?.cursor ? `?cursor=${encodeURIComponent(state.cursor)}&scope=${encodeURIComponent(state.scope || '')}&stream=${encodeURIComponent(state.stream_id || '')}` : '';
    const response = await this.request(`/sync/pull${query}`);
    this.check();
    if (!response.ok) throw new Error(response.status === 401 ? 'Sign in to refresh this workspace.' : `Refresh failed (${response.status}).`);
    const data = await response.json(); this.check();
    if (typeof data.cursor !== 'string' || typeof data.scope !== 'string') throw new Error('Server needs the current sync protocol. Local edits remain saved.');
    await database.transaction('rw', database.tables, async () => {
      const pending = await database.outbox.toArray();
      const routeGaps=new Set((await database.recordedRoutes.toArray()).filter(route=>route.needsSegment).map(route=>route.id));
      const protectedRecords = new Set(pending.filter(op=>['mapFeatures','gpsTrackPoints','recordedRoutes'].includes(op.table_name)).map(op => `${op.table_name}:${op.entity_id}`));
      if (data.reset && Array.isArray(data.tables)) {
        const legacy=(await database.workspaceMeta.get('recorded-routes'))?.value||[];
        const legacySegments=new Set(legacy.flatMap((r:any)=>r.segments||[]));
        for(const point of await database.gpsTrackPoints.toArray())if(!point.routeId&&legacySegments.has(point.sessionId))protectedRecords.add('gpsTrackPoints:'+point.id);
        const cacheKeys=await database.workspaceMeta.toCollection().primaryKeys();
        const accessChanged=!state?.scope||state.scope.split(':photo-access-v1:')[0]!==data.scope.split(':photo-access-v1:')[0];
        // Parent/photo changes routinely reset the photo projection. Keep an
        // independently prepared map unless account/property privileges changed.
        await database.workspaceMeta.bulkDelete(cacheKeys.filter(key=>['journal-cache:','journal-media:','route-community:','route-bookmarks:',...(accessChanged?['prepared-visit:','prepared-terrain:','badge-cache:']:[])].some(prefix=>String(key).startsWith(prefix))));
        const resetTables = new Set([...data.tables, ...(state?.scope || '').split(':')[0].split(',').filter(Boolean)]);
        for (const name of resetTables) {
          if (!database.tables.some(table => table.name === name)) continue;
          const table = database.table(name);
          const keys = await table.toCollection().primaryKeys();
          await table.bulkDelete(keys.filter(key => !protectedRecords.has(`${name}:${key}`)));
        }
      }
      const changes = Array.isArray(data.changes) ? data.changes : [];
      for (const change of changes) {
        if (!database.tables.some(table => table.name === change.table_name)) continue;
        if (protectedRecords.has(`${change.table_name}:${change.entity_id}`)) continue;
        const payload=workspaceRecord(change.table_name, change.payload);
        if(change.table_name==='recordedRoutes'){
          const local=await database.recordedRoutes.get(change.entity_id);
          await database.recordedRoutes.put({...payload,needsSegment:local?.needsSegment||routeGaps.has(change.entity_id)});
        }else await database.table(change.table_name).put(payload);
      }
      await database.syncState.put({ key: 'global_sync', cursor: data.cursor, scope: data.scope,
        stream_id: data.stream_id,
        last_pulled_at: null, last_sync_epoch: data.epoch || 0 });
    });
    this.check();
    if(data.reset&&typeof window!=='undefined'){
      window.dispatchEvent(new Event('sonata-photo-cache-purged'));
      window.dispatchEvent(new Event('sonata-route-cache-purged'));
      window.dispatchEvent(new Event('sonata-journal-updated'));
    }
  }
}

export async function resolveWorkspaceConflict(database: SonataDatabase, opId: string, choice: 'local' | 'server') {
  const conflict = await database.outbox.get(opId);
  if (!conflict || conflict.status !== 'conflict' || !conflict.server_entity) throw new Error('Conflict details are unavailable.');
  await database.transaction('rw', [database.table(conflict.table_name), database.outbox], async () => {
    const related = (await database.outbox.toArray()).filter(op => op.table_name === conflict.table_name && op.entity_id === conflict.entity_id);
    if (choice === 'server') {
      await database.table(conflict.table_name).put(conflict.server_entity);
      await database.outbox.bulkDelete(related.map(op => op.op_id));
    } else {
      // Consolidate the user's latest local version into a new idempotent operation.
      // The old mutation id must not be reused after changing its payload/base.
      const local = await database.table(conflict.table_name).get(conflict.entity_id);
      const latest = related.sort((a, b) => a.created_at_local.localeCompare(b.created_at_local)).at(-1)!;
      if (!local) throw new Error('Your saved local record is unavailable.');
      await database.outbox.bulkDelete(related.map(op => op.op_id));
      const id = crypto.randomUUID();
      await database.outbox.add({ ...latest, payload: conflict.table_name==='recordedRoutes'?routePayload(local):{ ...local, deleted_at: local.deleted_at || null }, type: local.deleted_at ? 'delete' : 'update', created_at_local: new Date().toISOString(), op_id: id, mutation_id: id, base_version: conflict.server_entity.server_version,
        status: 'pending', error: undefined, server_entity: undefined, retry_count: 0 });
    }
  });
}
